import { fetchInspectionPhotos } from "../reducers/InspectionsReducer";
import { getService } from "../reducers/service";
import { serviceNames } from "./constants";
import { decrypt } from "./encryptionDecryptionHelper";
import { getEmployeeOptionLabel } from "./functions";

export const getEmployees = async (userAgency, isOptions = false) => {
	const service = await getService(serviceNames.personnel);
	const response = await service.find({
		query: {
			$limit: -1,
			isDeleted: false,
			$sort: { lastName: 1, firstName: 1, middleName: 1 },
			agencyId: userAgency,
		},
	});

	if (isOptions) {
		return response?.map((employee) => ({
			label: getEmployeeOptionLabel(employee),
			value: decrypt(employee.id),
		}));
	}
	return response;
};

export const findEmployees = async (query) => {
	const service = await getService(serviceNames.personnel);
	const response = await service.find({
		query: {
			...query,
			isDeleted: false,
			$sort: { lastName: 1, firstName: 1, middleName: 1 },
		},
	});

	return response;
};

export const getFundingSources = async (userAgency, isOptions = false) => {
	const service = await getService(serviceNames.perFundingSources);
	const response = await service.find({
		query: {
			$limit: -1,
			isDeleted: false,
			agencyId: userAgency,
		},
	});

	if (isOptions) {
		return response?.map((fundingSources) => ({
			label: fundingSources?.title,
			value: fundingSources?.id,
		}));
	}
	return response;
};

export const getWorkOrders = async (userAgency) => {
	const service = await getService(serviceNames.perWorkOrders);
	const response = await service.find({
		query: {
			$limit: -1,
			isDeleted: false,
			agencyId: userAgency,
		},
	});

	return response;
};

export const getWorkOrdersByVehicle = async (vehicleId, userAgency) => {
	const service = await getService(serviceNames.perWorkOrders);
	const response = await service.find({
		query: {
			vehicleId,
			$limit: -1,
			isDeleted: false,
			agencyId: userAgency,
		},
	});

	return response;
};

export const getInspectionsByVehicle = async (vehicleId, userAgency) => {
	const service = await getService(serviceNames.perInspections);
	const response = await service.find({
		query: {
			vehicleId,
			$limit: -1,
			isDeleted: false,
			agencyId: userAgency,
		},
	});

	const inspectionsData = response || [];

	// Fetch responses for each inspection
	const responsesService = getService(serviceNames.perInspectionResponses);

	const inspectionsWithDetails = await Promise.all(
		inspectionsData.map(async (inspection) => {
			try {
				// Get responses
				const responsesResponse = await responsesService.find({
					query: {
						inspectionId: inspection.id,
						isDeleted: false,
						$limit: -1,
					},
				});

				const responses =
					responsesResponse?.data || responsesResponse || [];

				// Get photos using personnel-photos service
				const photos = await fetchInspectionPhotos(inspection.id);

				return {
					...inspection,
					responses: responses.map((r) => ({
						checklistItemId: r.checklistItemId,
						status: r.status,
						comment: r.comment,
					})),
					photos: photos,
				};
			} catch (error) {
				console.error(
					`Error fetching inspection details for ${inspection.id}:`,
					error,
				);
				return {
					...inspection,
					responses: [],
					photos: [],
				};
			}
		}),
	);

	return inspectionsWithDetails;
};

export const getVehicles = async (userAgency) => {
	const service = await getService(serviceNames.perVehicles);
	const response = await service.find({
		query: {
			$limit: -1,
			isDeleted: false,
			agencyId: userAgency,
		},
	});

	return response;
};

export const getEquipments = async (userAgency) => {
	const service = await getService(serviceNames.perEquipment);
	const response = await service.find({
		query: {
			$limit: -1,
			isDeleted: false,
			agencyId: userAgency,
		},
	});

	return response;
};

export const getEquipmentMaintenance = async (userAgency) => {
	const service = await getService(serviceNames.perEquipmentMaintenance);
	const response = await service.find({
		query: {
			$limit: -1,
			isDeleted: false,
			agencyId: userAgency,
		},
	});

	return response;
};

export const getDispensableItems = async (userAgency) => {
	const service = await getService(serviceNames.perDispensableItems);
	const response = await service.find({
		query: {
			$limit: -1,
			isDeleted: false,
			agencyId: userAgency,
		},
	});

	return response;
};

export const getEquipmentByVehicle = async (vehicleId, userAgency) => {
	const service = await getService(serviceNames.perEquipment);
	const response = await service.find({
		query: {
			$limit: -1,
			isDeleted: false,
			agencyId: userAgency,
			vehicleId: vehicleId,
		},
	});

	return response;
};

export const createVehicleAssignmentHistory = async (payload) => {
	try {
		const service = await getService(
			serviceNames.perVehicleAssignmentHistory,
		);
		const response = await service.create(payload);

		return response;
	} catch (error) {
		console.error("Error creating vehicle assignment history:", error);
		throw error;
	}
};

export const updateVehicleAssignmentHistory = async (id, data) => {
	try {
		const service = getService(serviceNames.perVehicleAssignmentHistory);
		const response = await service.patch(id, data);
		return response;
	} catch (error) {
		console.error("Error updating vehicle assignment history:", error);
		throw error;
	}
};

export const getVehicleAssignmentHistory = async (vehicleId, agencyId) => {
	try {
		const service = getService(serviceNames.perVehicleAssignmentHistory);
		const response = await service.find({
			query: {
				vehicleId,
				agencyId,
				isDeleted: false,
				$limit: -1,
				$sort: { assignedDate: -1 },
			},
		});
		return response;
	} catch (error) {
		console.error("Error getting vehicle assignment history:", error);
		throw error;
	}
};

// Find active assignment for a vehicle (no unassignedDate)
export const findActiveVehicleAssignment = async (vehicleId, agencyId) => {
	try {
		const service = getService(serviceNames.perVehicleAssignmentHistory);
		const response = await service.find({
			query: {
				vehicleId,
				agencyId,
				unassignedDate: null,
				isDeleted: { $ne: true },
				$limit: 1,
				$sort: { assignedDate: -1 },
			},
		});
		return response.data?.[0] || response[0] || null;
	} catch (error) {
		console.error("Error finding active vehicle assignment:", error);
		throw error;
	}
};

export const getVehicleNotes = async (userAgency) => {
	try {
		const service = await getService(serviceNames.perVehicleNotes);
		const response = await service.find({
			query: {
				$limit: -1,
				isDeleted: false,
				agencyId: userAgency,
			},
		});

		return response;
	} catch (e) {}
};

export const updateVehicleNote = async (id, data) => {
	try {
		const service = await getService(serviceNames.perVehicleNotes);
		const response = await service.patch(id, data);

		return response;
	} catch (e) {}
};

export const createVehicleNote = async (data) => {
	try {
		const service = await getService(serviceNames.perVehicleNotes);
		const response = await service.create(data);

		return response;
	} catch (e) {}
};

export const createAuditLog = async (data) => {
	try {
		const service = await getService(serviceNames.perAuditLog);
		const response = await service.create(data);

		return response;
	} catch (e) {}
};

export const getAuditLogs = async (
	page = 0,
	pageSize = 20,
	sortField = "changedAt",
	sortDirection = "desc",
) => {
	try {
		const service = await getService(serviceNames.perAuditLog);

		const query = {
			$skip: page * pageSize,
			$limit: pageSize,
			$sort: {
				[sortField]: sortDirection === "asc" ? 1 : -1,
			},
			isDeleted: false,
		};

		const response = await service.find({ query });
		return response;
	} catch (e) {
		console.error("Error fetching audit logs:", e);
		throw e;
	}
};
